import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, withKeys as _withKeys, createBlock as _createBlock, createSlots as _createSlots } from "vue"

const _hoisted_1 = {
  for: "input-code-string",
  class: "mb-1"
}
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = {
  key: 0,
  class: "text-success"
}
const _hoisted_9 = {
  key: 1,
  class: "text-danger"
}
const _hoisted_10 = {
  key: 0,
  class: "text-success"
}
const _hoisted_11 = {
  key: 1,
  class: "text-danger"
}

import { computed, ref, onMounted, watch } from 'vue';
import {
  BButton,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { useLazyQuery } from '@vue/apollo-composable';
import {
  CodeDocument,
  CodeType,
  CouponType,
  KitType,
  LicenseType,
} from '@/gql/graphql';
import { CouponTypeName, CouponUnitsName } from '@/constants';
import { deepParseDates, formatDate } from '@/utils/date-utils';

// --- Component Properties ---
export interface DefaultCode {
  codeString: string;
  unitsRequired: number;
}

// --- Component-Specific Types ---
interface CodeValidationItem {
  code: CodeType | null;
  codeString: string;
  state: boolean | null;
  evaluated: boolean;
  inProgress: boolean;
  showSuccess: boolean;
  message: string | null;
  unitsAvailable: number;
  unitsDeclared: number;
  unitsDeclaredInput: number;
}

// --- Local-Only State ---

export default /*@__PURE__*/_defineComponent({
  __name: 'CodeValidation',
  props: {
    defaultCodes: {},
    labelText: {},
    requireKit: { type: Boolean },
    requireExplicitCounts: { type: Boolean },
    requiredCouponTypeName: {},
    requriedCouponUnitsName: {},
    requiredCouponUnitsCount: {},
    requiredLicenseTypeName: {},
    immediateValidation: { type: Boolean },
    hideComponentMessage: { type: Boolean }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const singleCodeRequired = computed(() => {
  // only coupon that requires multiple units is not single
  return (
    !props.requireExplicitCounts &&
    (props.requiredCouponTypeName === null ||
      props.requriedCouponUnitsName === null ||
      props.requiredCouponUnitsCount <= 1)
  );
});

const codeValidationItems = ref<CodeValidationItem[]>(
  props.defaultCodes.length > 0
    ? props.defaultCodes.map((defaultCode: DefaultCode) => ({
        code: null,
        codeString: defaultCode.codeString,
        state: null,
        inProgress: false,
        evaluated: false,
        showSuccess: false,
        message: null,
        unitsAvailable: 0,
        unitsDeclared: defaultCode.unitsRequired,
        unitsDeclaredInput: defaultCode.unitsRequired,
      }))
    : [
        {
          code: null,
          codeString: '',
          state: false,
          inProgress: false,
          evaluated: false,
          showSuccess: false,
          message: 'Type code and click Apply to validate',
          unitsAvailable: 0,
          unitsDeclared: props.requiredCouponUnitsCount,
          unitsDeclaredInput: props.requiredCouponUnitsCount,
        },
      ]
);
const selectedItem = ref<CodeValidationItem | null>(null);

const state = ref<boolean | null>(null);
const fetched = ref(false); // so we know whether to fetch or re-fetch

// --- Exposed Properties to the Outsie ---
const message = ref<string | null>(null);
const success = computed(() => state.value === true);
const failure = computed(() => state.value !== true);
const split = computed(
  () =>
    !singleCodeRequired.value &&
    (codeValidationItems.value.length > 1 || props.requireExplicitCounts)
);

__expose({
  codeValidationItems,
  message,
  success,
  failure,
});

// Only update if the conversion produces a valid number.
function onUnitsDeclaredInputChanged(item: CodeValidationItem, value: string) {
  console.debug('Units declared input changed:', value);
  const num = Number(value);
  if (!isNaN(num) && num !== item.unitsDeclared && num > 0) {
    console.debug('Update units declared:', num);
    item.unitsDeclared = num;
    validateItem(item); // re-validate the item state
    validate(); // re-validate component state
    return;
  }
  // Otherwise, leave the old value unchanged.
  console.debug('Keep the original value:', value);
  item.unitsDeclaredInput = item.unitsDeclared;
}

// --- Mounted: Immediate Validation ---
onMounted(() => {
  if (props.immediateValidation && codeValidationItems.value) {
    codeValidationItems.value.forEach((item) => {
      if (item.codeString) {
        requestCode(item);
      }
    });
  }
});

// --- GraphQL Query Code ---
const {
  load: fetchCode,
  onResult: onCodeResult,
  onError: onCodeError,
  refetch: refetchCode,
} = useLazyQuery(CodeDocument);

function setCode(item: CodeValidationItem, update: CodeType) {
  // Replace the entire object to trigger eventual watcher in child components
  item.code = {
    ...update,
    kit: update.kit
      ? {
          ...(deepParseDates(update.kit) as KitType),
        }
      : undefined,
    coupons: update.coupons?.map((coupon) => ({
      ...(deepParseDates(coupon) as CouponType),
    })),
    license: update.license
      ? {
          ...(deepParseDates(update.license) as LicenseType),
        }
      : undefined,
  };
  // Validate the code and update the component state
  validateItem(item);
  validate();
}

function validateItem(item: CodeValidationItem) {
  // mark the code as evaluated
  item.evaluated = true;
  // assign default values to state variables
  let kitState = !props.requireKit;
  let couponState = props.requiredCouponTypeName === null;
  let licenseState = props.requiredLicenseTypeName === null;
  let feedbackPositive = [];
  let feedbackNegative = [];
  //*
  // KIT validation
  if (props.requireKit) {
    if (item.code?.kit) {
      kitState = true;
      feedbackPositive.push(
        `Kit ${item.codeString} identified successfuly (${item.code.kit.name}).`
      );
    } else {
      kitState = false;
      feedbackNegative.push(
        `No kit associated with the code ${item.codeString}.`
      );
    }
  }
  // COUPON validation
  if (props.requiredCouponTypeName) {
    // find specific coupon type and check expiration
    const coupon = item.code?.coupons?.find(
      (cpn) => cpn && cpn.couponType === props.requiredCouponTypeName
    );
    // check coupon exists
    if (!coupon) {
      item.unitsAvailable = 0;
      couponState = false;
      feedbackNegative.push(
        `No ${props.requiredCouponTypeName} credits associated with the code ${item.codeString}.`
      );
    }
    // check coupon expiration date
    else if (coupon.expiresAt && coupon.expiresAt <= Date.now()) {
      item.unitsAvailable = 0;
      couponState = false;
      feedbackNegative.push(
        `The code ${item.codeString} expired ${formatDate(coupon.expiresAt)}.`
      );
    }
    // success straight away if no units required are specified
    else if (props.requiredCouponUnitsCount === 0) {
      item.unitsAvailable = coupon.unitsRemaining || 0;
      couponState = true;
      feedbackPositive.push(`Code ${item.codeString} found successfuly.`);
    }
    // fail if units required but no available
    else if (!coupon.unitsRemaining) {
      item.unitsAvailable = 0;
      couponState = false;
      feedbackNegative.push(
        `No remaining ${props.requiredCouponTypeName} associated with the code ${item.codeString}.`
      );
    }
    // fail if less units available than declared
    else if (coupon.unitsRemaining < item.unitsDeclared) {
      item.unitsAvailable = coupon.unitsRemaining || 0;
      couponState = false;
      feedbackNegative.push(
        `Not enough ${props.requiredCouponTypeName} credits in the code ${item.codeString}, only ${item.unitsAvailable} remaining.`
      );
    }
    // succes otherwise
    else {
      item.unitsAvailable = coupon.unitsRemaining || 0;
      couponState = true;
      feedbackPositive.push(
        `Code ${item.codeString} contains ${coupon.unitsRemaining} of ${coupon.unitsTotal} ${coupon?.units} remaining.`
      );
    }
  }
  // LICENSE validation
  if (props.requiredLicenseTypeName) {
    // check license exists
    if (
      !item.code?.license ||
      item.code.license.licenseType !== props.requiredLicenseTypeName
    ) {
      licenseState = false;
      feedbackNegative.push(
        `No ${props.requiredCouponTypeName} license associated with the code ${item.codeString}.`
      );
    }
    // check license expiration
    else if (
      item.code.license.validUntil &&
      item.code.license.validUntil <= Date.now()
    ) {
      licenseState = false;
      feedbackNegative.push(
        `The license ${item.codeString} expired ${formatDate(
          item.code.license.validUntil
        )}.`
      );
    }
    // succes otherwise (license exists, not expired)
    else {
      licenseState = true;
      feedbackPositive.push(`Valid license identified.`);
    }
  }
  // compose the validation result
  item.state = couponState && kitState && licenseState;
  if (item.state) {
    item.message = feedbackPositive.join(' ');
  } else {
    item.message = feedbackNegative.join(' ');
  }
}

function validate() {
  // skip if in progress
  if (codeValidationItems.value.find((item) => item.inProgress)) {
    console.debug('Validation in progress, skipping');
    state.value = false;
    message.value = 'One or more codes validation is still in progress.';
    return;
  }
  // check if all items are evaluated
  if (
    codeValidationItems.value.find((item) => !item.evaluated || item.inProgress)
  ) {
    console.debug('Not all items evaluated, skipping');
    state.value = false;
    message.value = 'One or more codes not validated successfully.';
    return;
  }
  // check if all items are valid
  if (codeValidationItems.value.find((item) => item.state === false)) {
    console.debug('Not all items valid, skipping');
    state.value = false;
    message.value = 'One or more codes not validated successfully.';
    return;
  }
  // check all items sum up to requiredUnitsCount
  const totalUnits = codeValidationItems.value.reduce(
    (acc, item) => acc + item.unitsDeclared,
    0
  );
  console.debug(
    'Required units:',
    props.requiredCouponUnitsCount,
    props.requriedCouponUnitsName,
    ' total units declared/required:',
    totalUnits
  );
  if (totalUnits < props.requiredCouponUnitsCount) {
    console.debug(
      'Not enough',
      props.requriedCouponUnitsName,
      'available:',
      totalUnits
    );
    state.value = false;
    message.value = `The following action will consume ${props.requiredCouponUnitsCount} ${props.requriedCouponUnitsName} credit(s),
                     but only ${totalUnits} available or declared. Contact your account manager if you need additional credits issued.`;
    return;
  }
  // otherwise success, all items are valid, and enough units available
  console.debug('All items valid and enough units available');
  state.value = true;
  message.value = 'Code validation successful.';
}

// use watcher to spot when any item of the codeValidationItems changes its codeString
watch(
  () => codeValidationItems.value.map((item) => item.codeString),
  (newValues, oldValues) => {
    console.debug('One or more codeString values changed');
    newValues.forEach((newVal, iVal) => {
      const oldVal = oldValues[iVal];
      if (oldVal !== newVal) {
        const item = codeValidationItems.value[iVal];
        console.debug(
          'Checking',
          oldVal,
          '->',
          newVal,
          'of item',
          item.codeString
        );
        // reset the item validation state
        if (item) {
          item.evaluated = false;
          item.state = false;
          item.message = 'Click apply to re-validate the code';
          item.showSuccess = false;
        }
        // reset the whole validation state
        state.value = null;
        message.value = null;
      }
    });
  }
);

// --- Reset validation result whenever props change ---
watch(props, () => {
  console.debug('Input props changed, re-validate codes');
  // check number of required units, increase the declared units immediately if only one code
  if (codeValidationItems.value.length === 1) {
    if (
      props.requiredCouponUnitsCount >
        codeValidationItems.value[0].unitsDeclared ||
      !props.requireExplicitCounts
    ) {
      console.debug('Units declared changed:', props.requiredCouponUnitsCount);
      codeValidationItems.value[0].unitsDeclared =
        props.requiredCouponUnitsCount;
      codeValidationItems.value[0].unitsDeclaredInput =
        props.requiredCouponUnitsCount;
    }
  }
  // revalidate all codes and the component state
  codeValidationItems.value.forEach((item) => {
    console.debug('Validating item:', item);
    if (item.evaluated) {
      validateItem(item);
    }
  });
  validate();
});

// --- GraphQL Event Handlers ---
onCodeResult((res) => {
  console.debug('Code response retrieved');

  // check selected item is set
  if (!selectedItem.value) {
    if (codeValidationItems.value.length === 1) {
      console.debug(
        'No selected item to update, selecting first item (onCodeResult)'
      );
      selectedItem.value = codeValidationItems.value[0];
    } else {
      console.error('No selected item to update');
      return;
    }
  }

  // update the selected item with the code details
  selectedItem.value.inProgress = false;
  selectedItem.value.showSuccess = true;
  setTimeout(() => {
    if (selectedItem.value) {
      selectedItem.value.showSuccess = false;
    }
  }, 1000);

  // inspect data and update the selected item
  if (res.data) {
    if (res.data.code) {
      console.debug('Code details retrieved successfuly');
      setCode(selectedItem.value, res.data.code as CodeType);
    } else {
      console.error('Error, could not retrieve code details:', res.data);
      selectedItem.value.state = false;
      selectedItem.value.message = 'Invalid code';
    }
  }
});

onCodeError((err) => {
  console.error('Error fetching code (onCodeError):', err.message);

  // check selected item is set
  if (!selectedItem.value) {
    if (codeValidationItems.value.length === 1) {
      selectedItem.value = codeValidationItems.value[0];
      console.debug(
        'No selected item to update, selecting first item (onCodeError)'
      );
    } else {
      console.error('No selected item to update');
      return;
    }
  }

  // show success of the item
  selectedItem.value.inProgress = false;
  selectedItem.value.state = false;
  selectedItem.value.message = err.message;
  selectedItem.value.showSuccess = true;
  setTimeout(() => {
    if (selectedItem.value) {
      selectedItem.value.showSuccess = false;
    }
  }, 1000);

  return Promise.resolve();
});

function requestCode(item: CodeValidationItem) {
  // Set the selected item
  selectedItem.value = item;

  // Validate the code string first
  if (!selectedItem.value.codeString) {
    console.error('Invalid code');
    selectedItem.value.state = false;
    selectedItem.value.message = 'Invalid code';
    return;
  } else if (selectedItem.value.codeString.length < 4) {
    console.error('Code too short', selectedItem.value.codeString);
    selectedItem.value.state = false;
    selectedItem.value.message = 'Code too short';
    return;
  } else {
    // check for duplicate codes
    const duplicate = codeValidationItems.value.find(
      (i) =>
        i !== selectedItem.value &&
        i.codeString === selectedItem.value?.codeString
    );
    if (duplicate) {
      console.error('Duplicate code', selectedItem.value.codeString);
      selectedItem.value.state = false;
      selectedItem.value.message =
        'Duplicate code ' + selectedItem.value?.codeString;
      return;
    }
  }

  // Fetch the code details otherwise
  if (fetched.value) {
    console.debug('Re-fetching code details:', selectedItem.value.codeString);
    requestRefetchCode();
  } else {
    console.debug('Fetching code details:', selectedItem.value.codeString);
    fetched.value = true;
    const result = fetchCode(CodeDocument, {
      codeString: selectedItem.value.codeString,
    });
    if (result) {
      result
        .then((res) => {
          console.debug('Code details fetched:', res);
        })
        .catch((err) => {
          console.error('Error fetching code (catch):', err);
        });
    } else {
      console.error('Error fetching code (no result available):', result);
    }
  }
}

function requestRefetchCode() {
  if (!selectedItem.value) {
    if (codeValidationItems.value.length === 1) {
      selectedItem.value = codeValidationItems.value[0];
      console.debug(
        'No selected item to update, selecting first item (requestRefetchCode)'
      );
    } else {
      console.error('No selected item to update');
      return;
    }
  }
  selectedItem.value.inProgress = true;
  refetchCode({ codeString: selectedItem.value.codeString })
    ?.catch((err) => {
      if (selectedItem.value) {
        selectedItem.value.inProgress = false;
      }
      console.error('Refetch code failed:', err);
    })
    .then(() => {
      if (selectedItem.value) {
        selectedItem.value.inProgress = false;
      }
      console.debug('Refetched code');
    });
}

// --- Event Handlers ---
function handleAddCode() {
  // push new item into the list
  codeValidationItems.value.push({
    code: null,
    codeString: '',
    state: false,
    inProgress: false,
    evaluated: false,
    showSuccess: false,
    message: 'Type code and click Apply to validate',
    unitsAvailable: 0,
    unitsDeclared: 1,
    unitsDeclaredInput: 1,
  });
  validate();
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("label", _hoisted_1, [
      _createVNode(_component_font_awesome_icon, {
        icon: "ticket",
        class: "me-1"
      }),
      _createTextVNode(" " + _toDisplayString(props.labelText) + " ", 1),
      _createVNode(_component_font_awesome_icon, {
        id: "code-help-icon",
        icon: "question-circle",
        class: "ms-1 text-secondary",
        title: "Kit required"
      }),
      _createVNode(_unref(BTooltip), {
        target: "code-help-icon",
        triggers: "hover focus"
      }, {
        default: _withCtx(() => [
          (props.requireKit)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, " Kit is required for this operation. "))
            : (props.requiredCouponTypeName)
              ? (_openBlock(), _createElementBlock("span", _hoisted_3, " Kit or coupon with " + _toDisplayString(props.requiredCouponTypeName.toLowerCase()) + " credits with an equivalent of " + _toDisplayString(props.requiredCouponUnitsCount) + " " + _toDisplayString(props.requriedCouponUnitsName) + " is required for this operation. ", 1))
              : (props.requiredLicenseTypeName)
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                    _cache[0] || (_cache[0] = _createTextVNode(" License ", -1)),
                    _createElementVNode("strong", null, _toDisplayString(props.requiredLicenseTypeName.toLowerCase()), 1),
                    _cache[1] || (_cache[1] = _createTextVNode(" is required for this operation. ", -1))
                  ]))
                : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(codeValidationItems.value, (item, idx) => {
      return (_openBlock(), _createBlock(_unref(BFormGroup), {
        key: idx,
        class: "mb-2"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(BInputGroup), {
            id: `code-string-group-${idx}`,
            description: "Provide coupon or kit code."
          }, _createSlots({
            append: _withCtx(() => [
              (codeValidationItems.value.length > 1)
                ? (_openBlock(), _createBlock(_unref(BButton), {
                    key: 0,
                    variant: "danger",
                    onClick: 
              () => {
                codeValidationItems.value.splice(
                  codeValidationItems.value.indexOf(item),
                  1
                );
                validate();
              }
            
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_font_awesome_icon, { icon: "xmark" })
                    ]),
                    _: 1
                  }, 8, ["onClick"]))
                : _createCommentVNode("", true),
              _createVNode(_unref(BButton), {
                variant: "primary",
                onClick: ($event: any) => (requestCode(item)),
                disabled: item.inProgress,
                class: "apply-btn"
              }, {
                default: _withCtx(() => [
                  (item.inProgress)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
                        _cache[4] || (_cache[4] = _createTextVNode(" Retrieving code ", -1)),
                        _createVNode(_unref(BSpinner), {
                          small: "",
                          label: ""
                        })
                      ]))
                    : (item.showSuccess)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
                          _cache[5] || (_cache[5] = _createTextVNode(" Code checked ", -1)),
                          _createVNode(_component_font_awesome_icon, {
                            icon: ['fa', 'check-double'],
                            class: "me-2"
                          })
                        ]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_7, " Apply code "))
                ]),
                _: 2
              }, 1032, ["onClick", "disabled"])
            ]),
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                id: "input-code-string",
                name: "input-code-string",
                modelValue: item.codeString,
                "onUpdate:modelValue": ($event: any) => ((item.codeString) = $event),
                state: item.state,
                trim: "",
                onKeyup: _withKeys(_withModifiers(($event: any) => (requestCode(item)), ["prevent"]), ["enter"])
              }, null, 8, ["modelValue", "onUpdate:modelValue", "state", "onKeyup"]),
              (split.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createVNode(_unref(BButton), {
                      variant: "dark",
                      disabled: ""
                    }, {
                      default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                        _createTextVNode(" for ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_unref(BFormInput), {
                      modelValue: item.unitsDeclaredInput,
                      "onUpdate:modelValue": ($event: any) => ((item.unitsDeclaredInput) = $event),
                      type: "number",
                      min: "1",
                      class: "small-input",
                      onInput: ($event: any) => (onUnitsDeclaredInputChanged(item, $event.target.value)),
                      onKeyup: _withKeys(_withModifiers(($event: any) => (requestCode(item)), ["prevent"]), ["enter"])
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "onInput", "onKeyup"]),
                    _createVNode(_unref(BButton), {
                      variant: "dark",
                      disabled: ""
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(props.requriedCouponUnitsName), 1)
                      ]),
                      _: 1
                    })
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _: 2
          }, [
            (split.value)
              ? {
                  name: "prepend",
                  fn: _withCtx(() => [
                    _createVNode(_unref(BButton), {
                      variant: "dark",
                      disabled: ""
                    }, {
                      default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                        _createTextVNode(" Code ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["id"]),
          (item.message)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (item.state === true)
                  ? (_openBlock(), _createElementBlock("small", _hoisted_8, _toDisplayString(item.message), 1))
                  : _createCommentVNode("", true),
                (item.state === false)
                  ? (_openBlock(), _createElementBlock("small", _hoisted_9, _toDisplayString(item.message), 1))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 2
      }, 1024))
    }), 128)),
    (!singleCodeRequired.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createVNode(_unref(BButton), {
            variant: "primary",
            onClick: handleAddCode,
            style: {"width":"100%"}
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: "plus",
                class: "me-2"
              }),
              _cache[6] || (_cache[6] = _createTextVNode(" Add another code ", -1))
            ]),
            _: 1
          }),
          (!props.hideComponentMessage && message.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (message.value && state.value === true)
                  ? (_openBlock(), _createElementBlock("small", _hoisted_10, _toDisplayString(message.value), 1))
                  : _createCommentVNode("", true),
                (message.value && state.value === false)
                  ? (_openBlock(), _createElementBlock("small", _hoisted_11, _toDisplayString(message.value), 1))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

})